import type { TCPOffsetOption } from '@sb/motion-planning';

import { occupiedTooltipSlots } from '../implementations/CustomGripper/types/tooltipSlots';
import type { DeviceConfiguration, DeviceKind } from '../types';

export type TooltipOption = {
  tcpOption: TCPOffsetOption;
  name: string;
};

/**
 * How a device kind names the tooltips it publishes:
 *
 * - `none` -- publishes no tooltip, so no option ever belongs to the kind.
 * - `bareKind` -- a single tooltip, named `ee-<kind>`.
 * - `suffixedKind` -- one tooltip per configured tip or filled slot, each
 *   `ee-<kind>-<suffix>`; the bare name belongs to nothing.
 * - `wristCamera` -- the shared `wristCamera` option.
 */
export type TooltipNamingScheme =
  | 'none'
  | 'bareKind'
  | 'suffixedKind'
  | 'wristCamera';

/**
 * The naming scheme every device kind publishes tooltips under.
 *
 * The `Record` is exhaustive over `DeviceKind`, so a kind that does not exist
 * yet cannot be added without being classified here. That is the whole of the
 * compile-time guarantee: an existing kind that starts publishing a tooltip,
 * or changes the scheme it publishes under, alters no type here and compiles
 * clean. Only the drift test catches that, and only for additions it can see.
 *
 * There is deliberately no "no tooltip" default to fall through to. When
 * {@link optionBelongsToKind} denies a kind an option it really publishes, every
 * pose saved at that tooltip loses its `gripperID` and is left to resolve
 * against whatever equipment the option name happens to match.
 */
export const TOOLTIP_NAMING_SCHEMES: Record<DeviceKind, TooltipNamingScheme> = {
  Camera: 'wristCamera',
  CNCMachine: 'none',
  CustomBase: 'none',
  CustomDevice: 'none',
  CustomGripper: 'suffixedKind',
  CustomIO: 'none',
  EsabWeldMachine: 'none',
  EwellixLiftTLT: 'none',
  FTPServer: 'none',
  GPUTaskRunner: 'none',
  HapticDevice: 'none',
  InvalidConfiguration: 'none',
  MillerWeldMachine: 'none',
  MiraiController: 'none',
  ModbusTCPServer: 'none',
  MountingAdapter: 'bareKind',
  MountingBracket: 'bareKind',
  NoGripper: 'none',
  OnRobot2FG14: 'bareKind',
  OnRobot2FG7: 'bareKind',
  OnRobot3FG15: 'bareKind',
  OnRobotDualQuickChanger: 'suffixedKind',
  OnRobotHexE: 'bareKind',
  RobotiqFT300: 'bareKind',
  OnRobotScrewdriver: 'bareKind',
  OnRobotVGP20: 'bareKind',
  Robotiq2F: 'bareKind',
  RobotiqEPick: 'bareKind',
  RobotiqTSF85: 'none',
  S3Bucket: 'none',
  SchunkEGx: 'bareKind',
  StandardBotsRO1: 'none',
  WeldMachine: 'none',
  WeldPositioner: 'none',
  WristCamera: 'wristCamera',
};

const SCHEME_MATCHERS: Record<
  TooltipNamingScheme,
  (tcpOption: TCPOffsetOption, kind: DeviceKind) => boolean
> = {
  none: () => false,
  bareKind: (tcpOption, kind) => tcpOption === `ee-${kind}`,
  // Never the bare kind: a suffix names which tip or slot, and the kind alone
  // names no offset.
  suffixedKind: (tcpOption, kind) => tcpOption.startsWith(`ee-${kind}-`),
  wristCamera: (tcpOption) => tcpOption === 'wristCamera',
};

/**
 * Whether `tcpOption` is a name equipment of this `kind` publishes a tooltip
 * under. The counterpart to {@link getTooltipOptions} for callers holding only
 * a kind -- `RoutineRunnerState.equipmentState` rows carry `{id, kind, state}`
 * and no configuration.
 *
 * Where a kind's option set depends on its configuration (which changer slots
 * are filled, how many tooltips a custom gripper defines, whether a camera is
 * calibrated), this answers the widest set that kind can publish. It says a
 * name is of the right shape for the kind, not that the row currently offers
 * it.
 */
export function optionBelongsToKind(
  tcpOption: TCPOffsetOption,
  kind: DeviceKind,
): boolean {
  return SCHEME_MATCHERS[TOOLTIP_NAMING_SCHEMES[kind]](tcpOption, kind);
}

/**
 * The prefix a custom gripper spells its tooltip options with, under the
 * `suffixedKind` scheme its {@link TOOLTIP_NAMING_SCHEMES} entry gives it.
 *
 * The one spelling of a slot's option name, written by
 * {@link tooltipOptionForSlot} and read back by {@link tooltipSlotForOption},
 * so the two directions cannot drift apart.
 */
const CUSTOM_GRIPPER_TOOLTIP_PREFIX = 'ee-CustomGripper-';

/**
 * The option name the tooltip in a custom gripper's `slot` is published under.
 *
 * The slot number, the option's numeric suffix and the `<id>_tcp_<N>` link
 * number the URDF mints are one number.
 */
export function tooltipOptionForSlot(slot: number): TCPOffsetOption {
  return `${CUSTOM_GRIPPER_TOOLTIP_PREFIX}${slot}`;
}

/**
 * The custom gripper slot `tcpOption` names, or `undefined` when the option is
 * no custom gripper tooltip's.
 *
 * A slot number says which slot was named, never that a tooltip is in it. A
 * caller holding the gripper's list reads the slot itself to learn that, which
 * is what keeps "this slot is empty" apart from "this option belongs to
 * another device" -- two facts that resolve to different frames.
 */
export function tooltipSlotForOption(
  tcpOption: TCPOffsetOption,
): number | undefined {
  if (!optionBelongsToKind(tcpOption, 'CustomGripper')) {
    return undefined;
  }

  const suffix = tcpOption.slice(CUSTOM_GRIPPER_TOOLTIP_PREFIX.length);

  // Slot numbers are decimal digits; anything else under the prefix is a name
  // nothing here ever minted.
  return /^\d+$/.test(suffix) ? Number(suffix) : undefined;
}

/**
 * Every tooltip a piece of equipment defines, in the order and under the names
 * the tooltip dropdowns show. Mirrors the per-integration frontend
 * `getTCPOffsetOptions` without importing any frontend module, so the routine
 * runner and the SDK's tooltip endpoints can enumerate tooltips too.
 *
 * Equipment that defines no tooltip of its own returns nothing: an option only
 * belongs here if it names a real offset, since selecting one that does not
 * would silently fall back to the flange. Slots and tooltip lists that no
 * gripper fills are excluded for the same reason.
 *
 * Because of that, an empty result does NOT mean the equipment is gone -- a
 * configured row can legitimately offer zero tooltips (an uncalibrated camera,
 * a CustomGripper with no tooltips yet). Callers deciding whether a frame's
 * equipment still exists must check the equipment registry by id; option
 * membership answers only whether a frame is currently selectable.
 *
 * The switch is exhaustive over `DeviceConfiguration`: a new equipment kind
 * fails to compile until someone decides its tooltips here, so an answer from
 * this function is always knowledge, never ignorance.
 *
 * The caller adds the wrist flange, which is not equipment.
 *
 * Enumerating needs the configuration, so it switches per kind here;
 * {@link TOOLTIP_NAMING_SCHEMES} classifies the names those options take for
 * callers holding only a kind. The two are held together by test.
 */
export function getTooltipOptions(
  config: DeviceConfiguration,
): TooltipOption[] {
  switch (config.kind) {
    case 'CustomGripper':
      // By slot, not by position: an emptied slot publishes nothing, and
      // every surviving tooltip keeps the option name it was created with.
      return occupiedTooltipSlots(config.tooltips).map(({ slot, tooltip }) => ({
        tcpOption: tooltipOptionForSlot(slot),
        name: tooltip.name,
      }));

    case 'OnRobotDualQuickChanger': {
      // No bare `ee-OnRobotDualQuickChanger` option exists -- the changer
      // exposes each slot holding a gripper under its own suffix.
      const slots = [
        { gripper: config.grippers.primary, suffix: 'primary' },
        { gripper: config.grippers.secondary, suffix: 'secondary' },
      ] as const;

      return slots
        .filter(({ gripper }) => gripper.kind !== 'NoGripper')
        .map(({ gripper, suffix }) => ({
          tcpOption: `ee-${config.kind}-${suffix}` as TCPOffsetOption,
          name: gripper.name,
        }));
    }

    case 'Camera':
      // A camera offers a tooltip only once its extrinsics are calibrated
      // against the tool frame; a base-mounted or uncalibrated camera has no
      // offset of its own.
      if (
        config.extrinsicsId != null &&
        config.extrinsicsData[config.extrinsicsId]?.extrinsics != null &&
        config.extrinsicsData[config.extrinsicsId]?.kind === 'tcp'
      ) {
        return [{ tcpOption: 'wristCamera', name: config.name }];
      }

      return [];

    case 'WristCamera':
      return [{ tcpOption: 'wristCamera', name: config.name }];

    case 'NoGripper':
      // An empty quick-changer slot filler: an end-effector kind that
      // genuinely offers nothing, never an unknown one.
      return [];

    case 'OnRobot2FG7':
    case 'OnRobot2FG14':
    case 'OnRobot3FG15':
    case 'OnRobotVGP20':
    case 'OnRobotHexE':
    case 'RobotiqFT300':
    case 'OnRobotScrewdriver':
    case 'SchunkEGx':
    case 'Robotiq2F':
    case 'RobotiqEPick':
    case 'MountingAdapter':
    case 'MountingBracket':
      // Single-tip equipment: one tooltip, named for the kind.
      //
      // The last two compose into the wrist rather than hanging off it, but a
      // piece's own mounting face is a tooltip an operator can jog and teach
      // to, and the chain truncates there rather than running on to whatever
      // is mounted beyond it.
      return [{ tcpOption: `ee-${config.kind}`, name: config.name }];

    case 'EwellixLiftTLT':
    case 'CustomIO':
    case 'CustomDevice':
    case 'CustomBase':
    case 'InvalidConfiguration':
    case 'ModbusTCPServer':
    case 'FTPServer':
    case 'S3Bucket':
    case 'CNCMachine':
    case 'StandardBotsRO1':
    case 'WeldMachine':
    case 'MillerWeldMachine':
    case 'EsabWeldMachine':
    case 'WeldPositioner':
    case 'GPUTaskRunner':
    case 'MiraiController':
    case 'HapticDevice':
    case 'RobotiqTSF85':
      // Not an end effector: never defines a tooltip.
      return [];

    default: {
      const exhaustive: never = config; // ensure type exhaustive
      throw new Error(
        `Unexpected equipment kind ${JSON.stringify(exhaustive)}`,
      );
    }
  }
}
